import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  listPromoCodes, createPromoCode, updatePromoCode, deletePromoCode, listPackages
} from '../../services/adminApi';

const PromoCodesTab = () => {
  const [codes, setCodes] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);

  const [form, setForm] = useState({
    code: '', discount_type: 'percentage', discount_value: 10,
    applicable_packages: [], max_uses: '', max_uses_per_user: 1,
    expires_at: '', active: true, description: ''
  });

  useEffect(() => {
    Promise.all([listPromoCodes(), listPackages()]).then(([c, p]) => {
      setCodes(c.data);
      setPackages(p.data);
    }).catch(() => setError('Failed to load')).finally(() => setLoading(false));
  }, []);

  const reload = async () => {
    const res = await listPromoCodes();
    setCodes(res.data);
  };

  const create = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        max_uses_per_user: form.max_uses_per_user ? parseInt(form.max_uses_per_user) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      await createPromoCode(payload);
      setForm({ code: '', discount_type: 'percentage', discount_value: 10, applicable_packages: [], max_uses: '', max_uses_per_user: 1, expires_at: '', active: true, description: '' });
      setShowForm(false);
      await reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (code, active) => {
    try {
      await updatePromoCode(code.code, { ...code, active: !active });
      await reload();
    } catch (err) {
      setError('Failed to update');
    }
  };

  const del = async (code) => {
    if (!window.confirm(`Deactivate promo code ${code}?`)) return;
    try {
      await deletePromoCode(code);
      await reload();
    } catch (err) {
      setError('Failed');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  const togglePackage = (pkgId) => {
    setForm(prev => ({
      ...prev,
      applicable_packages: prev.applicable_packages.includes(pkgId)
        ? prev.applicable_packages.filter(p => p !== pkgId)
        : [...prev.applicable_packages, pkgId]
    }));
  };

  if (loading) return <div className="p-8 text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#009E60] border-t-transparent mx-auto"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">Create promo codes users can enter at checkout. Underlying prices stay unchanged.</p>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#009E60] hover:bg-[#007A4B] rounded-full" data-testid="new-promo-btn">
          <Plus className="w-4 h-4 mr-1" /> New Code
        </Button>
      </div>

      {error && <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm">{error}</div>}

      {showForm && (
        <div className="bg-[#009E60]/5 border-2 border-[#009E60]/20 rounded-2xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="GUYANA50" className="mt-1 rounded-xl font-mono" data-testid="promo-code-input" />
            </div>
            <div>
              <Label className="text-sm">Discount Type</Label>
              <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-gray-200 px-3 bg-white" data-testid="promo-type">
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed ($)</option>
              </select>
            </div>
            <div>
              <Label className="text-sm">Discount Value</Label>
              <Input type="number" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="mt-1 rounded-xl" data-testid="promo-value" />
            </div>
            <div>
              <Label className="text-sm">Expires At</Label>
              <Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label className="text-sm">Max Total Uses</Label>
              <Input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Unlimited" className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label className="text-sm">Max Per User</Label>
              <Input type="number" value={form.max_uses_per_user} onChange={(e) => setForm({ ...form, max_uses_per_user: e.target.value })} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div>
            <Label className="text-sm">Applicable Packages (leave empty for all)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {packages.map(p => (
                <button
                  key={p.package_id}
                  onClick={() => togglePackage(p.package_id)}
                  className={`px-3 py-1.5 rounded-full text-xs border-2 ${
                    form.applicable_packages.includes(p.package_id)
                      ? 'border-[#009E60] bg-[#009E60] text-white'
                      : 'border-gray-200 bg-white text-gray-700'
                  }`}
                >
                  {p.name} · ${p.amount}
                </button>
              ))}
            </div>
          </div>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (internal)" rows={2} className="rounded-xl resize-none" />
          <div className="flex gap-2">
            <Button onClick={() => setShowForm(false)} variant="outline" className="flex-1 rounded-xl">Cancel</Button>
            <Button onClick={create} disabled={saving || !form.code} className="flex-1 bg-[#009E60] hover:bg-[#007A4B] rounded-xl" data-testid="save-promo">
              {saving ? 'Creating...' : 'Create Code'}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {codes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No promo codes yet</div>
        ) : codes.map(code => (
          <div key={code.code} className="bg-white border rounded-2xl p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => copyCode(code.code)}
                  className="font-mono font-bold text-[#009E60] flex items-center gap-1 hover:underline"
                  data-testid={`code-${code.code}`}
                >
                  {code.code}
                  {copied === code.code ? <Check className="w-4 h-4" /> : <Copy className="w-3 h-3" />}
                </button>
                <span className="text-sm text-gray-500">
                  {code.discount_type === 'percentage' ? `${code.discount_value}% OFF` : `$${code.discount_value} OFF`}
                </span>
                {!code.active && <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">Inactive</span>}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Used: {code.uses_count || 0}{code.max_uses ? ` / ${code.max_uses}` : ''}
                {code.expires_at && ` · Expires ${new Date(code.expires_at).toLocaleDateString()}`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={code.active} onCheckedChange={() => toggleActive(code, code.active)} />
              <button onClick={() => del(code.code)} className="p-2 hover:bg-red-50 rounded-full text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PromoCodesTab;
